import React, { useState, useEffect } from 'react';
import './RegisterDetailsModal.css';
import apiService from '../../services/api';

const RegisterDetailsModal = ({ isOpen, onClose, userDetails }) => {
  const [currentDateTime, setCurrentDateTime] = useState('');
  const [businessInfo, setBusinessInfo] = useState(null);

  useEffect(() => {
    // Set date range based on screenshot pattern
    const now = new Date();
    const formattedDate = now.toLocaleString('en-GB', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true 
    }).replace(/,/g, '');
    
    // Mock the start date as roughly 1 day ago for demonstration
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const startFormatted = yesterday.toLocaleString('en-GB', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true 
    }).replace(/,/g, '');

    setCurrentDateTime(`${startFormatted} - ${formattedDate}`);
    
    // Load business details for the footer
    const loadBusinessInfo = async () => {
      try {
        const data = await apiService.getBusinessDetails();
        setBusinessInfo(data.data || data);
      } catch (error) {
        console.error('Error loading business info:', error);
      }
    };
    loadBusinessInfo();
  }, []);

  if (!isOpen) return null;

  // Mock data matching the screenshot exactly
  const paymentSummary = [
    { method: 'Cash in hand', sell: '0.00', expense: '0.00' },
    { method: 'Cash Payment', sell: '172.96', expense: '6.00' },
    { method: 'Cheque Payment', sell: '0.00', expense: '0.00' },
    { method: 'Card Payment', sell: '0.00', expense: '0.00' },
    { method: 'Bank Transfer', sell: '0.00', expense: '0.00' },
    { method: 'Advance payment', sell: '0.00', expense: '0.00' },
    { method: 'Other Payments', sell: '0.00', expense: '0.00' },
  ];

  const totals = [
    { label: 'Total Sales', value: '172.96', type: 'primary' },
    { label: 'Total Refund', value: '0.00', type: 'danger' },
    { label: 'Total Payment', value: '178.96', type: 'success' },
    { label: 'Credit Sales', value: '0.00', type: 'warning' },
    { label: 'Total Sales', value: '172.96', type: 'primary' }, // Matching screenshot duplicate
    { label: 'Total Expense', value: '6.00', type: 'danger' },
  ];

  const productSold = [
    { sku: '213123123', product: 'Budweiser Bottle 12pk', qty: '1.00', total: '15.99' },
    { sku: '23214151', product: 'Corona Extra 6pk', qty: '2.00', total: '24.50' },
    { sku: '12351235', product: 'Jack Daniels 750ml', qty: '1.00', total: '32.99' },
  ];

  const brandSold = [
    { no: '1', brand: 'Budweiser', qty: '1.00', total: '15.99' },
    { no: '2', brand: 'Corona', qty: '2.00', total: '24.50' },
    { no: '3', brand: 'Jack Daniels', qty: '1.00', total: '32.99' },
  ];

  return (
    <div className="rd-modal-overlay">
      <div className="rd-modal-container">
        <div className="rd-modal-header">
          <h2 className="rd-title">Register Details</h2>
          <span className="rd-date-range">({currentDateTime})</span>
          <button className="rd-close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="rd-modal-body">
          {/* Section 1: Payment Method Summary */}
          <section className="rd-section">
            <h3 className="rd-section-title">Payment Method Summary</h3>
            <div className="rd-table-responsive">
              <table className="rd-table">
                <thead>
                  <tr>
                    <th>Payment Method</th>
                    <th>Sell ($)</th>
                    <th>Expense ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentSummary.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.method}</td>
                      <td>{item.sell}</td>
                      <td>{item.expense}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 2: Totals Snapshot */}
          <section className="rd-section">
            <div className="rd-totals-grid">
              {totals.map((item, idx) => (
                <div key={idx} className={`rd-total-card rd-total-card--${item.type}`}>
                  <span className="rd-total-label">{item.label}:</span>
                  <span className="rd-total-value">$ {item.value}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Section 3: Details of products sold */}
          <section className="rd-section">
            <h3 className="rd-section-title">Details of products sold</h3>
            <div className="rd-table-responsive">
              <table className="rd-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>SKU</th>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Total amount ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {productSold.map((item, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{item.sku}</td>
                      <td>{item.product}</td>
                      <td>{item.qty}</td>
                      <td>{item.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 4: Details of products sold (By Brand) */}
          <section className="rd-section">
            <h3 className="rd-section-title">Details of products sold (By Brand)</h3>
            <div className="rd-table-responsive">
              <table className="rd-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Brand</th>
                    <th>Quantity</th>
                    <th>Total amount ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {brandSold.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.no}</td>
                      <td>{item.brand}</td>
                      <td>{item.qty}</td>
                      <td>{item.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 5: Signature & Location */}
          <div className="rd-footer-info">
            <div className="rd-footer-item">
              <strong>User:</strong> {userDetails?.data?.first_name} {userDetails?.data?.last_name || '(Admin)'}
            </div>
            <div className="rd-footer-item">
              <strong>Email:</strong> {userDetails?.data?.email || '(N/A)'}
            </div>
            <div className="rd-footer-item">
              <strong>Business Location:</strong> {businessInfo?.name || 'Main Warehouse'}
            </div>
          </div>
        </div>

        <div className="rd-modal-actions">
          <button className="rd-btn rd-btn--print" onClick={() => window.print()}>
            <svg viewBox="0 0 24 24" width="16" height="16"><path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z"/></svg>
            Print
          </button>
          <button className="rd-btn rd-btn--close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default RegisterDetailsModal;
